import React, { useState, useEffect, useRef } from "react";
import { ChevronDown } from "lucide-react";

const Dropdown = ({ options, value, onChange, className = "", buttonClassName = "", renderOption, renderSelected, align = "left" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
    }
  }, [isOpen]);

  const getOptionVal = (opt) => {
    if (!opt) return undefined;
    return opt.id !== undefined ? opt.id : opt.value;
  };

  const selectedOption = options.find((opt) => getOptionVal(opt) === value)
    || options.find((opt) => getOptionVal(opt) === '')
    || options[0];

  const visibleOptions = options.filter(opt => {
    const val = getOptionVal(opt);
    const label = opt.name || opt.label || '';
    if ((val === '' || val === null || val === undefined) && /^select/i.test(label)) {
      return false;
    }
    return true;
  });

  const filteredOptions = visibleOptions.filter(opt => {
    const label = opt.name || opt.label || '';
    return label.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-5 py-2.5 bg-white border border-gray-100 rounded-xl text-xs text-gray-900 cursor-pointer flex justify-between items-center hover:bg-gray-50 transition-all font-bold uppercase tracking-wider ${buttonClassName}`}
        style={selectedOption?.style}
      >
        <span>
          {renderSelected ? renderSelected(selectedOption) : (selectedOption?.name || selectedOption?.label)}
        </span>
        <ChevronDown
          size={16}
          className={`text-gray-400 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
        />
      </div>

      {isOpen && (
        <div className={`absolute ${align === 'right' ? 'right-0' : 'left-0'} top-full mt-2 bg-white border border-gray-100 rounded-2xl z-50 shadow-2xl overflow-hidden animate-slideUp min-w-[200px] w-full flex flex-col`}>
          {options.length > 6 && (
            <div className="p-2 border-b border-gray-100 bg-gray-50/50 sticky top-0 z-10">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search options..."
                className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900/10 placeholder:text-gray-400 font-medium transition-all"
                onClick={(e) => e.stopPropagation()} // Prevent closing dropdown on click
              />
            </div>
          )}
          
          <div className="max-h-[250px] overflow-y-auto divide-y divide-gray-50/50">
            {filteredOptions.length === 0 ? (
              <div className="px-5 py-3.5 text-xs text-gray-400 italic">No matches found</div>
            ) : (
              filteredOptions.map((option, index) => {
                const optVal = option.id !== undefined ? option.id : (option.value !== undefined ? option.value : `opt_${index}`);
                const isSelected = value === optVal;
                return (
                  <div
                    key={optVal}
                    onClick={() => {
                      if (option.disabled) return;
                      onChange(optVal);
                      setIsOpen(false);
                    }}
                    className={`px-5 py-3 text-xs font-bold uppercase tracking-wider transition-all ${
                      option.disabled 
                        ? "text-gray-400 cursor-default bg-gray-50" 
                        : isSelected 
                          ? "bg-blue-50 text-blue-600 cursor-pointer hover:bg-blue-50" 
                          : "text-gray-700 cursor-pointer hover:bg-blue-50 hover:text-blue-600"
                    }`}
                    style={option.style}
                  >
                    {renderOption ? renderOption(option, isSelected) : (option.name || option.label)}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dropdown;
